import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { NextSeo } from 'next-seo';
import cn from 'classnames';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import { getProductBySlug } from '@adapters/bigcommerce/operations/product';
import {
  getAllCustomerGroupPrices,
  MultiGroupPrices,
} from '@adapters/bigcommerce/operations/getAllCustomerGroupPrices';
import type { BigCommerceLocaleString } from '@adapters/bigcommerce/types/locale';
import {
  MecColour,
  MecImage,
  MecProduct,
} from '@adapters/bigcommerce/types/product';
import { ALGOLIA_CHECKOUT_QUERY_ID_NAME } from '@adapters/bigcommerce/constants';

import {
  FitSizeValueInterface,
  SizeChartInterface,
} from '@adapters/bigcommerce/types/sizeChart';
import { getProductAnswerCount } from '@adapters/bazaarvoice/getProductAnswerCount';
import { getProductFitSentimentData } from '@adapters/bazaarvoice/getProductFitSentimentData';
import getAllPromoTickerData from '@adapters/prismic/fetching/promos/getAllPromoTickerData';
import getBannerStripGlobalData from '@adapters/prismic/fetching/banner-strip-global/getBannerStripGlobalData';
import { PromoTicker } from '@adapters/prismic/types/promoTicker';
import Layout from '@components/global/Layout';
import Container from '@components/global/Container';
import Breadcrumbs from '@components/algolia/Breadcrumbs';
import BreadcrumbsV2 from '_components/Breadcrumbs';
import { getBreadcrumbHrefs } from '@utils/algolia/getBreadcrumbHrefs';
import Heading from '@components/ui/Heading';
import ProductMetaInfo from '_components/Product/ProductMetaInfo';
import ClipboardCopy from '_components/Product/ClipboardCopy';
import VideoBlock from '_components/Product/VideoBlock';

import klaviyoProductView from '@adapters/klaviyo/operations/analytics/viewedProduct';
import { PdpContextProvider } from '@context/PdpContext';
import { GiftCardContextProvider } from '@context/GiftCardContext';
import { StructuredSearchData } from '@components/product/StructuredSearchData';
import { getAllFitAndRise } from '@adapters/prismic/fetching/productMeasure/getAllFitAndRise';
import { getSizeChartDataFromS3 } from '@adapters/bigcommerce/operations/getSizeChartDataFromS3';
import { Card } from '@components/plp/FindifyCollectionsLoadingTakeover';
import { getAllStoresDataForSetMyStore } from '@adapters/prismic/fetching/stores';
import { useStorePc } from '@hooks/useStorePc';
import { slugify, localePath, localePathAbs } from '@utils/url';
import { altLocale } from '@utils/locale';
import { truncateDescription } from '@utils/seo';
import { getInitialColourId } from '@utils/pdp/getInitialColourId';
import { GetServerSidePropsContext, NextApiRequest } from 'next';
import Script from 'next/script';
import RecommendationsLoader, {
  AlgonomyPlacement,
  PRODUCT_MINI_SINGLE,
  PRODUCT_MINI_CAROUSEL,
} from '@components/global/Recommendations/Loader';
import useObjectVisibility from '@hooks/useObjectVisibility';
import useOutfittingLoadState from '@hooks/useOutfittingLoadState';
import usePdp2026ScrollInteraction, {
  Pdp2026ScrollState,
} from '@hooks/usePdp2026ScrollInteraction';
import loadingStyles from '@components/plp/FindifyCollectionsLoadingTakeover/loadingStyles.module.scss';
import Sticky from 'react-stickynode';
import ProductNotInStockSection from '@components/product/ProductNotInStockSection';
import ProductNotInStockSectionV2 from '_components/Product/ProductNotInStockSection';
import ProductBuyZone from '@components/product/ProductBuyZone';
import ProductBuyZoneV2 from '_components/Product/ProductBuyZone';
import StockLevelModal from '@components/product/StockLevelModal';
import StoreInformationModal from '_components/Product/FindInStore/StoreInformationModal';
import DeliveryMethodModal from '@components/cart/DeliveryMethodModal';
import CompareBar from '_components/CompareBar';
import ProductStorePC from '@components/product/ProductStorePC';
import Skeleton from 'react-loading-skeleton';
import { decodeOptions } from '@utils/optimizeAbTest/utils';
import TagsContainer from '@components/ui/Tags';
import { useUI } from '@context/GlobalUIContext';
import getProductTagInfoData from '@adapters/prismic/fetching/productTagInfo/getProductTagInfo';
import { ProductTagInfo } from '@adapters/prismic/types/productTagInfo';
import getOutfittingTileData from '@adapters/prismic/fetching/outfittingTile/getOutfittingTileData';
import getResolvedOutfittingTile from '@adapters/prismic/utils/getResolvedOutfittingTile';
import { ResolvedOutfittingTile } from '@adapters/prismic/types/outfittingTile';
import OutfittingTile from '_components/Outfitting/OutfittingTile';
import OutfittingAnchor from '_components/Product/OutfittingAnchor';
import { getOutfitsFromPlacement } from '@components/global/Recommendations/outfits';
import { PDP_OUTFIT_PLACEMENT } from '@components/global/Recommendations/Loader';
import ProductPriceAndReviewsContainer from '@components/product/ProductPriceAndReviewsContainer';
import ProductCollectionComponent from '@components/product/ProductCollectionComponent';
import { handleSelectItemTracking } from '@utils/gtm';
import { GA4_EVENT_NAMES } from '@utils/gtm/ga4/constants';
import AlbyPDP from '@components/product/AlbyPDP';
import handleCompareTableClickTracking from '@utils/gtm/ga4/handleCompareTableClickTracking';

import {
  getAlgonomyPathFeatureFlag,
  FEATURE_FLAG_ALBY_CHATBOT,
  FEATURE_FLAG_MARKETPLACE,
  FEATURE_FLAG_MEMBER_PRICING,
  FEATURE_FLAG_PDP_IMAGE_GALLERY,
  FEATURE_FLAG_PROMO_BAR_SERVER_SIDE,
  FEATURE_FLAG_PDP_2026_1,
  marketplacerFFCookieName,
  FEATURE_FLAG_PDP_REVIEWS_AND_ANSWERS,
  FEATURE_FLAG_NEW_PRODUCT_STRIP,
} from '@config/feature-flag';
import trackAlbyEvent, { AlbyEventName } from '@utils/gtm/ga4/trackAlbyEvents';
import prismicPdpCompare from '../../../prismic-pdp-compare.json';
import ComparisonCheckbox from '@components/global/Comparison/ComparisonCheckbox';
import { getPreviewRef } from '@adapters/prismic/utils/getPreviewRef';
import PdpMediaRenderer from '_components/PdpMediaRenderer';

import styles from './Product.module.scss';
import { marketplaceFlag } from '../../../featureFlags';

import ProductComparison, {
  PRODUCT_NAME_EN,
  PRODUCT_NAME_FR,
  PRODUCT_PRICE_EN,
  PRODUCT_PRICE_FR,
} from '@components/product/ProductComparison';
import ProductCompareTable from '_components/Product/ProductCompareTable';
import {
  buildGroupPricesByCode,
  buildProductCompareConfig,
  CompareSpec,
} from '@utils/comparison/buildProductCompareTableProps';
import getPromoBarData from '@adapters/prismic/fetching/promos/getPromoBarData';
import { PromoBarDocumentData } from 'prismicio-types';
import SectionAccordion from '_components/UI/SectionAccordion';

const { SELECT_ITEM_COOKIE } = GA4_EVENT_NAMES;

interface AlbyThreadCreatedEvent extends CustomEvent {
  detail: {
    threadId: string;
    widgetId: string;
  };
}

const ProductInfoSection = dynamic(
  () => import('@components/product/ProductInfoSection')
);

const ProductReviews = dynamic(
  () => import('@components/product/ProductReviews')
);

const ProductReviewsAndAnswersContainer = dynamic(
  () => import('_components/Product/ProductReviewsAndAnswersContainer')
);

const ProductAskAnswer = dynamic(
  () => import('@components/product/ProductAskAnswer')
);

const Recommendations = dynamic(
  () => import('@components/global/Recommendations'),
  {
    loading: () => (
      <div className={loadingStyles.rightCol}>
        {[...Array(12)].map((e, i) => (
          <Card key={i} />
        ))}
      </div>
    ),
  }
);
const ProductStrip = dynamic(() => import('_components/ProductStrip'));

export const DEFAULT_PDP_CACHE_TIME = '3600';

interface ProductProps {
  time: any;
  product: MecProduct;
  productRelativePath: string;
  canonicalUrl: string;
  xDefault: string;
  langAltUrl: string;
  productCode: string;
  fitSentimentData: any;
  answerCount: number;
  promoMessages?: PromoTicker[];
  measureData: SizeChartInterface;
  fitAndRise: FitSizeValueInterface[] | null;
  initialImage?: number;
  abVariant: string | null;
  productColours: MecColour[];
  productTagInfoData: ProductTagInfo;
  IS_ALGONOMY_PATH_CAT_ID_ENABLED: boolean;
  customerGroupPrices: MultiGroupPrices | null;
  compareProductsGroupPrices: Record<string, MultiGroupPrices | null> | null;
  productCompareData: {
    title: {
      en: string;
      fr: string;
    };
    productCompareSpecs: CompareSpec[];
    productCompareProductIds: string[];
  };
  isBazaarvoiceStaging: boolean;
  initialColourId?: number | null;
  outfittingTileConfig: ResolvedOutfittingTile | null;
}

export async function getServerSideProps({
  req,
  res,
  params,
  previewData,
  preview = false,
  query,
}: GetServerSidePropsContext<{ locale: string; pdp: string }>) {
  const locale: BigCommerceLocaleString = params?.locale === 'fr' ? 'fr' : 'en';
  let options;
  if (params) {
    try {
      options = decodeOptions(params?.pdp);
    } catch (error) {
      console.error(`Error while parsing params: ${params}`, error);
    }
  }
  const abVariant = options?.abVariant ?? null;
  const productCode = options?.code;
  const marketplaceEnabled = await marketplaceFlag(req);

  if (!productCode) {
    console.error(`No code specified for product`);

    return {
      notFound: true,
    };
  }

  const queryVariables = {
    slug: productCode,
    locale,
  };
  const previewRef = getPreviewRef(preview, previewData);

  // Compare-table config only depends on productCode (a static Prismic
  // lookup), so it's computed up front and its price fetch can join the
  // main parallel batch below instead of waiting on it to finish.
  const productCompareSpecs = [
    { en: PRODUCT_NAME_EN, fr: PRODUCT_NAME_FR },
    { en: PRODUCT_PRICE_EN, fr: PRODUCT_PRICE_FR },
    ...(prismicPdpCompare[productCode as keyof typeof prismicPdpCompare]
      ?.specs ?? []),
  ];

  const productCompareProductIds =
    prismicPdpCompare[productCode as keyof typeof prismicPdpCompare]
      ?.compare_product_ids;

  let productCompareData = null;
  if (productCompareProductIds) {
    productCompareData = {
      title:
        prismicPdpCompare[productCode as keyof typeof prismicPdpCompare]?.title,
      productCompareSpecs,
      productCompareProductIds,
    };
  }

  const [
    product,
    customerGroupPrices,
    stores,
    fitSentimentData,
    answerCount,
    promoMessages,
    bannerStripData,
    serverSideTranslation,
    productTagInfoData,
    promoBarData,
    compareProductsGroupPrices,
  ] = (await Promise.allSettled([
    getProductBySlug(queryVariables, options?.colour),
    FEATURE_FLAG_MEMBER_PRICING
      ? getAllCustomerGroupPrices(productCode, locale)
      : Promise.resolve(null),
    getAllStoresDataForSetMyStore(locale),
    getProductFitSentimentData(productCode),
    getProductAnswerCount(productCode),
    !FEATURE_FLAG_PROMO_BAR_SERVER_SIDE &&
      locale &&
      getAllPromoTickerData(locale),
    getBannerStripGlobalData(locale, preview ? previewRef : undefined),
    serverSideTranslations(locale, [
      'common',
      'pdp',
      'promotions',
      'productMeasure',
      'stores',
      'comparison',
    ]),
    getProductTagInfoData(locale),
    FEATURE_FLAG_PROMO_BAR_SERVER_SIDE && locale && getPromoBarData(locale),
    FEATURE_FLAG_MEMBER_PRICING && productCompareProductIds?.length
      ? Promise.allSettled(
          productCompareProductIds.map((id) =>
            getAllCustomerGroupPrices(id, locale)
          )
        ).then((compareGroupResults) =>
          Object.fromEntries(
            productCompareProductIds.map((id, i) => [
              id,
              compareGroupResults[i].status === 'fulfilled'
                ? (
                    compareGroupResults[
                      i
                    ] as PromiseFulfilledResult<MultiGroupPrices>
                  ).value
                : null,
            ])
          )
        )
      : Promise.resolve(null),
  ]).then((results) =>
    results.map((promise) => {
      if (
        promise.status === 'rejected' ||
        typeof promise.value === 'undefined'
      ) {
        console.error('promise rejected or returning undefined', promise);
        return null;
      }
      return promise.value;
    })
  )) as [
    MecProduct | null,
    MultiGroupPrices | null,
    ReturnType<typeof getAllStoresDataForSetMyStore>,
    ReturnType<typeof getProductFitSentimentData>,
    ReturnType<typeof getProductAnswerCount>,
    PromoTicker[],
    ReturnType<typeof serverSideTranslations>,
    MecImage | null,
    ProductTagInfo,
    PromoBarDocumentData['messages'] | null | undefined,
    Record<string, MultiGroupPrices | null> | null
  ];

  const measureGender = product?.featureData;
  const sizeDetail = product?.sizeData;

  const [measureData, fitAndRise] = await Promise.allSettled([
    (sizeDetail?.size_chart &&
      getSizeChartDataFromS3(sizeDetail?.size_chart)) ??
      null,
    (measureGender?.gender &&
      getAllFitAndRise(
        locale,
        measureGender?.gender,
        preview ? previewRef : undefined
      )) ??
      null,
  ]).then((results) =>
    results.map((promise) => {
      if (
        promise.status === 'rejected' ||
        typeof promise.value === 'undefined'
      ) {
        console.error('promise rejected or returning undefined', promise);
        return null;
      }
      return promise.value;
    })
  );

  if (!product || product.isDisabled) {
    console.error(`Product with code '${productCode}' not found`);

    return {
      notFound: true,
    };
  }

  if (!product.isVisible) {
    return {
      redirect: {
        destination: `/${locale}${
          product.breadcrumbs[product.breadcrumbs.length - 1].path
        }`,
      },
    };
  }

  if (product?.isDiscontinued && !product?.isInStock) {
    res.statusCode = 410;
  }

  const path = `/product/${productCode}/${slugify(product.slug)}`;

  const queryParams = Object.entries(query).reduce((acc: string[][], curr) => {
    const [key, value] = curr;
    if (!key || !value) {
      return acc;
    }
    if (
      key === 'pdp' ||
      key === 'locale' ||
      key === 'nextInternalLocale' ||
      key === 'queryId'
    ) {
      // exclude values
      return acc;
    }
    if (Array.isArray(value)) {
      return [...acc, [key, value.toString()]];
    }

    return [...acc, [key, value]];
  }, []);

  const search = new URLSearchParams(queryParams).toString();

  // Variant selection (e.g. ?colour=, ?size=) stays in the URL for UX via
  // productRelativePath, but must not appear in SEO signals. Canonical and
  // hreflang URLs always point to the clean, parameter-free product URL so
  // search engines consolidate authority and avoid duplicate content.
  const productRelativePath = `${localePath(locale, path)}${
    search ? `?${search}` : ''
  }`;

  const canonicalUrl = localePathAbs(locale, path);

  const langAltUrl = `${process.env.BASE_URL}${product?.hreflang ?? ''}`;

  const alternateLocaleUrl = langAltUrl;
  const xDefault =
    locale === 'en'
      ? localePathAbs(locale, path)
      : `${process.env.BASE_URL}${product?.hreflang ?? ''}`;

  const IS_ALGONOMY_PATH_CAT_ID_ENABLED = getAlgonomyPathFeatureFlag(
    req as NextApiRequest
  );

  const isBazaarvoiceStaging = Boolean(
    process.env.BAZAARVOICE_API_URL?.includes('stg.')
  );

  // Normalize colour query parameter (Next.js can return string | string[] | undefined)
  const colourParam = Array.isArray(query.colour)
    ? query.colour[0]
    : query.colour;

  // Determine initial color selection to prevent image flash during hydration
  const initialColourId = getInitialColourId(product?.colours, colourParam);

  const outfittingTileConfig = product
    ? getResolvedOutfittingTile(getOutfittingTileData(locale), {
        brand: product.brand?.name ?? null,
        categoryTargets: product.categoryTargets ?? [],
        productCode: productCode ?? null,
      })
    : null;

  const result = {
    props: {
      ...serverSideTranslation,
      product,
      outfittingTileConfig,
      customerGroupPrices,
      stores,
      productRelativePath,
      canonicalUrl,
      xDefault,
      langAltUrl,
      productCode,
      fitSentimentData,
      answerCount,
      alternateLocaleUrl,
      promoMessages,
      bannerStripData,
      measureData,
      fitAndRise,
      abVariant,
      productTagInfoData,
      IS_ALGONOMY_PATH_CAT_ID_ENABLED,
      productCompareData,
      compareProductsGroupPrices,
      featureFlags: {
        marketplaceEnabled,
      },
      promoBarData,
      isBazaarvoiceStaging,
      initialColourId,
    },
  };
  return result;
}

export default function ProductPage({
  product: pureProduct,
  canonicalUrl,
  xDefault,
  langAltUrl,
  productRelativePath,
  productCode,
  fitSentimentData,
  answerCount,
  measureData,
  fitAndRise,
  abVariant,
  IS_ALGONOMY_PATH_CAT_ID_ENABLED,
  productCompareData,
  compareProductsGroupPrices,
  customerGroupPrices,
  isBazaarvoiceStaging,
  initialColourId,
  outfittingTileConfig,
}: ProductProps) {
  // Filter out marketplace variants that are not MEC
  const product = React.useMemo(() => {
    if (!pureProduct || !Array.isArray(pureProduct.variants))
      return pureProduct;

    // If marketplace feature flag is enabled, do not filter variants
    if (
      FEATURE_FLAG_MARKETPLACE ||
      Cookies.get(marketplacerFFCookieName) === 'true'
    ) {
      return pureProduct;
    }

    const filteredVariants = pureProduct.variants.filter(
      (variant) =>
        !variant.marketplaceSellerName ||
        variant.marketplaceSellerName === 'MEC'
    );
    // Recalculate isInStock based on filtered variants
    const isInStock = filteredVariants.some((v) => v.hasStockAvailable);
    return {
      ...pureProduct,
      variants: filteredVariants,
      isInStock,
    };
  }, [pureProduct]);

  useEffect(() => {
    handleCompareTableClickTracking();
  }, []);

  const BuyZone = FEATURE_FLAG_PDP_2026_1 ? ProductBuyZoneV2 : ProductBuyZone;
  const PDPContainer = FEATURE_FLAG_PDP_2026_1 ? 'div' : Container;

  const albyRef = useRef<HTMLDivElement>(null);
  const { openTagInfoModal } = useUI();
  const router = useRouter();
  const { asPath, query } = router;
  const locale = (query.locale as string) || 'en';

  const recommendationsRef = useRef<any>(null);
  const recommendationsRefValue = useObjectVisibility(recommendationsRef, 0.05);
  const [isRecommendationsRef, setIsRecommendationsRef] = useState(false);

  const productInfoRef = useRef<HTMLDivElement>(null);
  const productInfoRefValue = useObjectVisibility(productInfoRef, 0.05);
  const [isProductInfoRef, setIsProductInfoRef] = useState(false);

  const reviewsRef = useRef<HTMLDivElement>(null);
  const reviewsRefValue = useObjectVisibility(reviewsRef, 0.05);
  const [isReviewsRef, setIsReviewsRef] = useState(false);
  const pdp2026ContainerRef = useRef<HTMLDivElement>(null);
  const pdp2026LeftColumnRef = useRef<HTMLDivElement>(null);
  const pdp2026RightColumnRef = useRef<HTMLDivElement>(null);

  const [openReviewsAccordion, setOpenReviewsAccordion] = useState(false);
  const [reviewsClicked, setReviewsClicked] = useState(false);
  const isStorePc = useStorePc();
  const [recommendations, setRecommendations] = useState<AlgonomyPlacement[]>(
    []
  );
  const [hasLoadedRecommendations, setHasLoadedRecommendations] =
    useState(false);

  const outfits = getOutfitsFromPlacement(
    recommendations.find((rec) => rec?.name === PDP_OUTFIT_PLACEMENT)
  );

  const isOutfittingLoading = useOutfittingLoadState(hasLoadedRecommendations);

  // Anchor renders (disabled) while outfits are loading, then either enables
  // or unmounts — it must never point at a tile that isn't on the page.
  const showOutfittingAnchor =
    Boolean(outfittingTileConfig?.anchorEnabled) &&
    Boolean(outfittingTileConfig?.anchorHeading) &&
    Boolean(outfittingTileConfig?.anchorBody) &&
    (isOutfittingLoading || outfits.length > 0);

  const outfittingTileRef = useRef<HTMLDivElement>(null);
  const scrollToOutfittingTile = () => {
    outfittingTileRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  const isDedicatedPlacement = (name?: string) =>
    name === 'item_page.collection1_rr' ||
    name === PDP_OUTFIT_PLACEMENT ||
    name === PRODUCT_MINI_SINGLE ||
    name === PRODUCT_MINI_CAROUSEL;

  const {
    isActive: isPdp2026ScrollActive,
    scrollState: pdp2026ScrollState,
    isLeftColumnFixed,
    isLeftColumnScrolling,
    leftColumnStickyTop,
    isRightColumnFixed,
  } = usePdp2026ScrollInteraction({
    enabled: FEATURE_FLAG_PDP_2026_1,
    containerRef: pdp2026ContainerRef,
    leftColumnRef: pdp2026LeftColumnRef,
    rightColumnRef: pdp2026RightColumnRef,
    galleryItemCount: product?.images?.length,
  });

  const buildPdpCollectionData = (
    pdpCollection: MecProduct['pdpCollection'],
    algonomyData: AlgonomyPlacement[]
  ) => {
    const algonomyCollection = algonomyData.find(
      (rec) => rec.name === 'item_page.collection1_rr'
    );
    return { ...algonomyCollection, ...pdpCollection };
  };

  const pdpCollectionData = buildPdpCollectionData(
    product?.pdpCollection,
    recommendations
  );

  useEffect(() => {
    const currentSearchParams = new URLSearchParams(window.location.search);

    const [path, existingQueryString] = productRelativePath.split('?');
    const existingSearchParams = existingQueryString
      ? new URLSearchParams(existingQueryString)
      : new URLSearchParams();

    currentSearchParams.forEach((value, key) => {
      existingSearchParams.set(key, value);
    });

    const mergedQueryString = existingSearchParams.toString();
    const finalPath = mergedQueryString ? `${path}?${mergedQueryString}` : path;

    window.history.replaceState(null, '', finalPath);
  }, [productRelativePath]);

  useEffect(() => {
    const handleThreadCreated = (e: Event) => {
      const { threadId } = (e as AlbyThreadCreatedEvent).detail;
      if (threadId) {
        trackAlbyEvent({
          eventName: AlbyEventName.THREAD_CREATED,
          path: window.location.pathname,
        });
      }
    };

    document.addEventListener(
      'alby:GenerativeQA::ThreadCreated',
      handleThreadCreated
    );

    return () => {
      document.removeEventListener(
        'alby:GenerativeQA::ThreadCreated',
        handleThreadCreated
      );
    };
  }, []);

  useEffect(() => {
    handleSelectItemTracking(SELECT_ITEM_COOKIE);
  }, []);

  useEffect(() => {
    if (!isProductInfoRef) setIsProductInfoRef(productInfoRefValue);
  }, [isProductInfoRef, productInfoRefValue]);

  useEffect(() => {
    if (!isReviewsRef) setIsReviewsRef(reviewsRefValue);
  }, [isReviewsRef, reviewsRefValue]);

  useEffect(() => {
    if (!FEATURE_FLAG_PDP_2026_1 && !isRecommendationsRef)
      setIsRecommendationsRef(recommendationsRefValue);
  }, [isRecommendationsRef, recommendationsRefValue]);

  useEffect(() => {
    // This is meant to mimic the behavior we had previously with a query param in the URL. Instead if any
    // link is clicked on the page, we will clear the query id from the cookies.
    // Debounced to prevent blocking INP on rapid interactions with buttons/inputs.
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const handleRouteChange = (ev: Event) => {
      const target =
        ev.target instanceof HTMLAnchorElement
          ? ev.target
          : ev.target instanceof Element && ev.target.closest('a');
      if ((target && target.href === window.location.href) || target === null) {
        // exit if the click is not on a link or if the link is the same as the current page
        return;
      }

      // Defer cookie removal to prevent blocking main thread during interaction
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        Cookies.remove(ALGOLIA_CHECKOUT_QUERY_ID_NAME);
      }, 0);
    };

    window.addEventListener('click', handleRouteChange, { passive: true });
    return () => {
      window.removeEventListener('click', handleRouteChange);
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    const urlContainsReviews = window.location.href.indexOf('#reviews') !== -1;
    setOpenReviewsAccordion(urlContainsReviews);
    setReviewsClicked(urlContainsReviews);
  }, []);

  const scrollToReviews = useCallback(() => {
    setReviewsClicked(true);
    setOpenReviewsAccordion(true);
  }, []);
  // const scrollToProductReviewsAndAnswers = () => {
  //   setProductReviewsAndAnswersClicked(true);
  //   setOpenReviewsAccordion(true);
  // };

  useEffect(() => {
    if (reviewsClicked && openReviewsAccordion) {
      reviewsRef?.current?.scrollIntoView({
        behavior: 'smooth',
      });
      setReviewsClicked(false);
    }
  }, [reviewsClicked, openReviewsAccordion]);

  const onAccordionToggle = useCallback((state: string) => {
    if (state === 'open') {
      setOpenReviewsAccordion(true);
    } else if (state === 'closed') {
      setOpenReviewsAccordion(false);
    }
  }, []);

  const { t } = useTranslation(['pdp', 'common']);

  const restrictions = product?.shippingRestrictions;
  const hasRestrictions =
    restrictions?.message_en &&
    restrictions?.message_fr &&
    restrictions?.message_type;
  // TODO: Add Skeleton Views

  const seoTitle = product?.name
    ? t('pdp.seo.title', {
        productName: product?.name,
      })
    : '';

  // Strip HTML once (regex over the full description) rather than twice on
  // every render; only recompute when the description actually changes.
  const cleanedDescription = useMemo(
    () => product?.description.replace(/(<([^>]+)>)/gi, '') ?? '',
    [product?.description]
  );

  const seoDescription = `${product?.seoName}: ${cleanedDescription}`;

  const truncatedDescription = `${product?.seoName}: ${truncateDescription(
    cleanedDescription
  )}`;

  let heroImage = product.images.find((img) => img.is_thumbnail === true);
  if (!heroImage) heroImage = product.images[0];

  // Build the compare-table props once instead of inline in JSX (which created
  // fresh objects on every render and defeated ProductCompareTable's memo).
  const compareGroupPricesByCode = useMemo(
    () =>
      buildGroupPricesByCode(
        product,
        customerGroupPrices,
        compareProductsGroupPrices
      ),
    [product, customerGroupPrices, compareProductsGroupPrices]
  );

  const compareTableConfig = useMemo(
    () =>
      productCompareData
        ? buildProductCompareConfig(
            productCompareData.productCompareSpecs,
            productCompareData.title,
            productCompareData.productCompareProductIds,
            locale
          )
        : null,
    [productCompareData, locale]
  );

  const pdpMediaCarousel = (
    <div
      ref={pdp2026LeftColumnRef}
      className={cn(styles.carousel, {
        [styles.pdp2026LeftColumnFixed]:
          isPdp2026ScrollActive && isLeftColumnFixed,
        [styles.pdp2026LeftColumnScrolling]:
          isPdp2026ScrollActive && isLeftColumnScrolling,
      })}
      style={
        isPdp2026ScrollActive && leftColumnStickyTop !== null
          ? ({
              '--pdp-2026-left-column-top': `${leftColumnStickyTop}px`,
            } as React.CSSProperties)
          : undefined
      }
    >
      <PdpMediaRenderer
        imageGallery={product?.images}
        madeInCanada={product?.madeInCanada}
        isCanadianBrand={product?.isCanadianBrand}
      />
    </div>
  );

  return (
    <>
      <NextSeo
        title={seoTitle}
        description={truncatedDescription}
        canonical={canonicalUrl}
        languageAlternates={[
          { hrefLang: altLocale(locale), href: langAltUrl },
          { hrefLang: String(locale), href: canonicalUrl },
          { hrefLang: 'x-default', href: xDefault },
        ]}
        openGraph={{
          title: seoTitle,
          description: truncatedDescription,
          locale,
          images: [
            {
              url: heroImage?.urlOriginal,
            },
          ],
        }}
      />
      <Script
        strategy="afterInteractive"
        src={`https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=${process.env.NEXT_PUBLIC_KLAVIYO_KEY}`}
        onLoad={() => {
          klaviyoProductView(product, canonicalUrl);
        }}
      />
      <Script
        id="bvScriptElement"
        strategy="lazyOnload"
        src={`https://apps.bazaarvoice.com/deployments/mec/main_site/${
          isBazaarvoiceStaging ? 'staging' : 'production'
        }/${locale}_CA/bv.js`}
      />
      <PDPContainer
        className={cn(styles.pdpOuterContainer2026, {
          [styles.pdpOuterContainer2026Enabled]: FEATURE_FLAG_PDP_2026_1,
        })}
      >
        {FEATURE_FLAG_PDP_2026_1 ? (
          <BreadcrumbsV2
            items={getBreadcrumbHrefs(product?.breadcrumbs ?? [], locale).map(
              (bcc) => ({
                id: bcc.entityId?.toString() ?? bcc.name,
                label: bcc.name,
                href: bcc.path,
              })
            )}
            productName={product?.name ?? null}
          />
        ) : (
          <Breadcrumbs
            breadcrumbCategories={
              product?.breadcrumbs.map((bcc) => bcc.name) ?? []
            }
            productName={product?.name ?? null}
          />
        )}
        <div
          id="contentContainer"
          ref={pdp2026ContainerRef}
          data-pdp-scroll-state={pdp2026ScrollState}
          className={cn(styles.pdpContainer, {
            [styles.pdpContainerOutOfStock]: !product?.isInStock,
            [styles.pdpContainerGalleryFirst]: FEATURE_FLAG_PDP_IMAGE_GALLERY,
            [styles.pdpContainer2026]: FEATURE_FLAG_PDP_2026_1,
            [styles.pdpContainer2026StateIdle]:
              FEATURE_FLAG_PDP_2026_1 &&
              pdp2026ScrollState === Pdp2026ScrollState.Idle,
            [styles.pdpContainer2026StateRightPanelScroll]:
              FEATURE_FLAG_PDP_2026_1 &&
              pdp2026ScrollState === Pdp2026ScrollState.RightPanelScrollToCta,
            [styles.pdpContainer2026StateLeftScroll]:
              FEATURE_FLAG_PDP_2026_1 &&
              pdp2026ScrollState === Pdp2026ScrollState.LeftGalleryScroll,
            [styles.pdpContainer2026StateRightSecondaryScroll]:
              FEATURE_FLAG_PDP_2026_1 &&
              pdp2026ScrollState === Pdp2026ScrollState.RightSecondaryScroll,
            [styles.pdpContainer2026StateFullPage]:
              FEATURE_FLAG_PDP_2026_1 &&
              pdp2026ScrollState === Pdp2026ScrollState.FullPageScroll,
          })}
        >
          <PdpContextProvider
            product={product}
            productCode={productCode}
            IS_ALGONOMY_PATH_CAT_ID_ENABLED={IS_ALGONOMY_PATH_CAT_ID_ENABLED}
            hasComparison={
              productCompareData !== null && productCompareData !== undefined
            }
            customerGroupPrices={customerGroupPrices}
            initialColourId={initialColourId}
          >
            <StructuredSearchData
              product={product}
              productCode={productCode}
              canonicalUrl={canonicalUrl}
              seoTitle={seoTitle}
              seoDescription={seoDescription}
            />
            <RecommendationsLoader
              pageType="pdp"
              data={{
                productName: product?.name,
                productCode,
                isInStock: product?.isInStock,
              }}
              onLoad={(data) => {
                setRecommendations(data);
                setHasLoadedRecommendations(true);
              }}
              trigger={!!product?.name && !!productCode}
            />
            <GiftCardContextProvider giftCard={product?.giftCard}>
              {FEATURE_FLAG_PDP_IMAGE_GALLERY && pdpMediaCarousel}

              <div
                ref={pdp2026RightColumnRef}
                className={cn(styles.pdp2026RightColumn, {
                  [styles.pdp2026RightColumnFixed]:
                    isPdp2026ScrollActive && isRightColumnFixed,
                })}
                // The sticky-top CSS variable (--pdp-2026-right-column-top) is
                // written directly to this element by usePdp2026ScrollInteraction
                // to avoid a React re-render on every scroll frame.
              >
                <Sticky
                  enabled={!FEATURE_FLAG_PDP_2026_1}
                  innerClass={styles.stickyInnerWrapper}
                  top={130}
                  bottomBoundary="#contentContainer"
                  className={styles.sidebar}
                >
                  {FEATURE_FLAG_PDP_2026_1 ? (
                    <div className={styles.productMetaInfo}>
                      <ProductMetaInfo
                        product={product}
                        productCode={productCode}
                        reviewsLink={`#reviews`}
                        scrollToProductReviewsAndAnswers={scrollToReviews}
                      />
                    </div>
                  ) : (
                    <div className={styles.productMetaInfoLegacy}>
                      <div className={styles.brandAndComparison}>
                        {product.brandURL &&
                          product.brand?.name &&
                          product.brand.name !== 'N/A' && (
                            <div className={styles.brandURL}>
                              <a href={product.brandURL}>
                                {product.brand.name}
                              </a>
                            </div>
                          )}
                        <ComparisonCheckbox
                          product={{ ...product, code: productCode }}
                          isPdp
                        />
                      </div>

                      <Heading
                        styleLevel="3"
                        semanticLevel="h1"
                        className={styles.productName}
                      >
                        {product.name}
                      </Heading>

                      <ClipboardCopy copyText={productCode} product={product} />

                      <div className={styles.productTagsContainer}>
                        <TagsContainer
                          tags={product?.productTags}
                          clickHandler={openTagInfoModal}
                          id={product?.code}
                        />
                      </div>
                    </div>
                  )}

                  {(product?.isInStock || !FEATURE_FLAG_PDP_2026_1) && (
                    <div
                      className={
                        FEATURE_FLAG_PDP_2026_1
                          ? styles.productPriceAndReviewsContainer
                          : cn(
                              styles.priceReviewsContainer,
                              styles.priceReviewContainerInStock
                            )
                      }
                    >
                      <ProductPriceAndReviewsContainer
                        isGiftCard={product.isGiftCard}
                        reviewSummary={product.reviewSummary}
                        name={product.name}
                        scrollToReviews={scrollToReviews}
                      />
                    </div>
                  )}

                  {!FEATURE_FLAG_PDP_2026_1 && !product?.isInStock && (
                    <ProductNotInStockSection
                      breadcrumbs={product?.breadcrumbs}
                    />
                  )}

                  {FEATURE_FLAG_PDP_2026_1 && !product?.isInStock && (
                    <ProductNotInStockSectionV2
                      breadcrumbs={product?.breadcrumbs}
                      recommendations={recommendations}
                    />
                  )}

                  <div className={styles.buyZone}>
                    {product.isInStock && (
                      <BuyZone
                        product={product}
                        productRelativePath={productRelativePath}
                        measureData={measureData}
                        fitAndRise={fitAndRise}
                        fitSentimentData={fitSentimentData}
                        hasRestrictions={hasRestrictions}
                        restrictions={restrictions}
                        abVariant={abVariant}
                        albyRef={albyRef}
                        productCode={productCode}
                        scrollToProductReviewsAndAnswers={scrollToReviews}
                        recommendations={recommendations}
                        outfittingAnchor={
                          showOutfittingAnchor ? (
                            <div className={styles.outfittingAnchorContainer}>
                              <OutfittingAnchor
                                heading={
                                  outfittingTileConfig?.anchorHeading ?? ''
                                }
                                body={outfittingTileConfig?.anchorBody ?? ''}
                                iconName={
                                  outfittingTileConfig?.anchorIconName ||
                                  undefined
                                }
                                productName={product?.name}
                                disabled={isOutfittingLoading}
                                onSelect={scrollToOutfittingTile}
                                data-testid="outfitting-anchor"
                              />
                            </div>
                          ) : null
                        }
                      />
                    )}
                  </div>
                </Sticky>
              </div>

              {!FEATURE_FLAG_PDP_IMAGE_GALLERY && pdpMediaCarousel}
              <StockLevelModal />
              <StoreInformationModal />
              <DeliveryMethodModal />
            </GiftCardContextProvider>

            <div className={styles.productInfo}>
              {locale === 'en' && FEATURE_FLAG_ALBY_CHATBOT && (
                <div ref={albyRef}>
                  <AlbyPDP productCode={productCode} />
                </div>
              )}

              {!FEATURE_FLAG_PDP_2026_1 && (
                <div ref={productInfoRef} style={{ minHeight: '600px' }}>
                  {isProductInfoRef ? (
                    <ProductInfoSection
                      description={product?.description}
                      isGiftCard={product?.isGiftCard}
                      techSpecs={product?.techSpecs}
                      infoVideoId={product?.infoVideoId}
                      productCompareData={productCompareData}
                      product={product}
                    />
                  ) : (
                    <Container>
                      <div className={styles.sliceZoneLoading}>
                        <Skeleton height={24} width={103} />
                      </div>
                      <div style={{ marginBottom: '16px' }}>
                        <Skeleton height={100} width="100%" />
                      </div>
                      {[...Array(8)].map((e, i) => (
                        <div
                          key={`product-info-loading-${i}`}
                          style={{ marginBottom: '8px' }}
                        >
                          <Skeleton height={24} width={600} />
                        </div>
                      ))}
                    </Container>
                  )}
                </div>
              )}

              {!FEATURE_FLAG_PDP_2026_1 && (
                <>
                  {product.quebecRepairDisclosureFlag && (
                    <div
                      className={styles.qcText}
                      dangerouslySetInnerHTML={{
                        __html: t('qc.bill29.text', { ns: 'common' }),
                      }}
                    />
                  )}

                  {(isStorePc || asPath.includes('?storepc=true')) && (
                    <ProductStorePC productNotes={product?.productNotes} />
                  )}

                  {!FEATURE_FLAG_PDP_REVIEWS_AND_ANSWERS && (
                    <div ref={reviewsRef}>
                      {isReviewsRef && !product?.isGiftCard ? (
                        <ProductReviews
                          productCode={productCode}
                          productReviewData={product?.reviewSummary}
                          productName={product?.name}
                          reviewsAccordionOpen={openReviewsAccordion}
                          onAccordionToggle={onAccordionToggle}
                        />
                      ) : (
                        <div style={{ marginBottom: '2px' }}>
                          <Skeleton height={60} width="100%" />
                        </div>
                      )}
                      {isReviewsRef ? (
                        <ProductAskAnswer
                          productCode={productCode}
                          answerCount={answerCount}
                        />
                      ) : (
                        <div style={{ marginBottom: '2px' }}>
                          <Skeleton height={60} width="100%" />
                        </div>
                      )}
                    </div>
                  )}

                  {pdpCollectionData?.displayCollection &&
                    pdpCollectionData?.items &&
                    pdpCollectionData?.items?.length > 0 && (
                      <ProductCollectionComponent
                        placement={pdpCollectionData}
                      />
                    )}
                  {!FEATURE_FLAG_PDP_2026_1 && <div ref={recommendationsRef} />}
                  {!FEATURE_FLAG_NEW_PRODUCT_STRIP &&
                    (recommendations.length ? (
                      recommendations?.map(
                        (recommendation: any) =>
                          !isDedicatedPlacement(recommendation?.name) && (
                            <Recommendations
                              isHome
                              placement={recommendation}
                              key={recommendation.name}
                            />
                          )
                      )
                    ) : (
                      <div className={loadingStyles.rightCol}>
                        {[...Array(12)].map((e, i) => (
                          <Card key={`recommendation-${i}`} />
                        ))}
                      </div>
                    ))}
                </>
              )}
            </div>
          </PdpContextProvider>
        </div>

        {!FEATURE_FLAG_PDP_2026_1 && productCompareData && (
          <div className={styles.productComparisonContainer}>
            <SectionAccordion
              icon="plus"
              className={styles.productComparisonSection}
              isOpen={true}
            >
              <SectionAccordion.Header
                className={styles.productComparisonHeader}
                title={
                  <span className={styles.productComparisonTitle}>
                    {t('product.comparison.title')}
                  </span>
                }
              />
              <SectionAccordion.Collapsible>
                <ProductComparison
                  product={product}
                  productCompareSpecs={productCompareData?.productCompareSpecs}
                  productCompareProductIds={
                    productCompareData?.productCompareProductIds
                  }
                />
              </SectionAccordion.Collapsible>
            </SectionAccordion>
          </div>
        )}
      </PDPContainer>

      {FEATURE_FLAG_PDP_2026_1 && product?.infoVideoId && (
        <VideoBlock
          className={styles.infoVideoContainer}
          videoId={product.infoVideoId}
          videoTitle={product.name}
          interactionLocation="video block"
        />
      )}

      {FEATURE_FLAG_PDP_2026_1 && (
        <div className={styles.reviewAndAnswer}>
          {(isStorePc || asPath.includes('?storepc=true')) && (
            <ProductStorePC productNotes={product?.productNotes} />
          )}
          {!FEATURE_FLAG_PDP_REVIEWS_AND_ANSWERS && (
            <div ref={reviewsRef}>
              {isReviewsRef && !product?.isGiftCard ? (
                <ProductReviews
                  productCode={productCode}
                  productReviewData={product?.reviewSummary}
                  productName={product?.name}
                  reviewsAccordionOpen={openReviewsAccordion}
                  onAccordionToggle={onAccordionToggle}
                />
              ) : (
                <div style={{ marginBottom: '2px' }}>
                  <Skeleton height={60} width="100%" />
                </div>
              )}
              {isReviewsRef ? (
                <ProductAskAnswer
                  productCode={productCode}
                  answerCount={answerCount}
                />
              ) : (
                <div style={{ marginBottom: '2px' }}>
                  <Skeleton height={60} width="100%" />
                </div>
              )}
            </div>
          )}

          {FEATURE_FLAG_PDP_2026_1 &&
            productCompareData &&
            compareTableConfig && (
              <div className={styles.productComparisonContainer}>
                <ProductCompareTable
                  isFranchise={false} // TODO: Update when we have franchise data for the pdp compare
                  product={product}
                  groupPricesByCode={compareGroupPricesByCode}
                  config={compareTableConfig}
                />
              </div>
            )}

          {pdpCollectionData?.displayCollection &&
            pdpCollectionData?.items &&
            pdpCollectionData?.items?.length > 0 && (
              <ProductCollectionComponent placement={pdpCollectionData} />
            )}
          {!FEATURE_FLAG_PDP_2026_1 && <div ref={recommendationsRef} />}
          {!FEATURE_FLAG_NEW_PRODUCT_STRIP &&
            (recommendations.length ? (
              recommendations?.map(
                (recommendation: any) =>
                  !isDedicatedPlacement(recommendation?.name) && (
                    <Recommendations
                      isHome
                      placement={recommendation}
                      key={recommendation.name}
                    />
                  )
              )
            ) : (
              <div className={loadingStyles.rightCol}>
                {[...Array(12)].map((e, i) => (
                  <Card key={`recommendation-${i}`} />
                ))}
              </div>
            ))}
        </div>
      )}
      {FEATURE_FLAG_NEW_PRODUCT_STRIP &&
        product?.isInStock &&
        recommendations
          ?.filter(
            (recommendation: any) => !isDedicatedPlacement(recommendation?.name)
          )
          .map((recommendation: any, index: number) => (
            <React.Fragment key={recommendation.name}>
              <ProductStrip placement={recommendation} />
              {index === 0 && (
                <div
                  ref={outfittingTileRef}
                  className={styles.outfittingTileContainer}
                >
                  <OutfittingTile
                    config={outfittingTileConfig}
                    outfits={outfits}
                    isLoading={isOutfittingLoading}
                  />
                </div>
              )}
            </React.Fragment>
          ))}
      {FEATURE_FLAG_PDP_REVIEWS_AND_ANSWERS && (
        <div
          id="reviews-and-answers"
          className={styles.reviewsAndAnswersContainer}
          ref={reviewsRef}
        >
          <ProductReviewsAndAnswersContainer
            productCode={productCode}
            productReviewData={product?.reviewSummary}
            openReviewsAccordion={openReviewsAccordion}
          />
        </div>
      )}
      <CompareBar />
    </>
  );
}

ProductPage.Layout = Layout;
